import { DEFAULT_CURRENCY } from '@/constants';
import usePageProps from '@/hooks/usePageProps';
import {
  useGetCurrencyRatesListQuery,
  useGetOrganizationBySlugQuery,
  useGetOrganizationsListQuery,
  useGetProductsListQuery,
} from '@/queries';

export const sortByKey = (array, key, direction = 'asc') =>
  [...array].sort((a, b) => {
    const aVal = a?.[key];
    const bVal = b?.[key];

    if (aVal === undefined || bVal === undefined) return 0;

    if (direction === 'desc') {
      return bVal - aVal;
    }

    return aVal - bVal;
  });

export function groupAndCollectByKey(items, key) {
  const map = new Map();

  items?.forEach((item) => {
    const groupKey = item[key];
    const existing = map.get(groupKey);

    if (!existing) {
      // Якщо перший елемент і він з isPriorityMinfin — ставимо його
      map.set(groupKey, { ...item });
    } else {
      // Якщо новий елемент має isPriorityMinfin, а існуючий — ні
      // eslint-disable-next-line no-lonely-if
      if (item.isPriorityMinfin && !existing.isPriorityMinfin) {
        const otherItems = [{ ...existing }, ...(existing.other ?? [])];
        map.set(groupKey, { ...item, other: otherItems });
      } else {
        (existing.other ??= []).push(item);
      }
    }
  });

  return Array.from(map.values());
}

function getUniqueByKey(arr, key) {
  const seen = new Set();

  return arr.filter((item) => {
    const value = item[key];
    if (seen.has(value)) return false;
    seen.add(value);

    return true;
  });
}

const conformitySort = {
  lowestRate: {
    key: 'loanRateYear',
  },
  longestTerm: {
    key: 'termMonthsMax',
    direction: 'desc',
  },
  biggestAmount: {
    key: 'loanLimitNumberMax',
    direction: 'desc',
  },
};

export const useGetProductsWithCompany = ({ locale }) => {
  const pageProps = usePageProps();
  const { params } = pageProps;
  const companySlug = pageProps?.params?.companySlug;

  const { data: companies } = useGetOrganizationsListQuery({ locale });
  const { data: singleCompanyData } = useGetOrganizationBySlugQuery(
    { locale, slug: companySlug },
    { skip: !companySlug }
  );
  const { data: products, ...rest } = useGetProductsListQuery({
    locale,
    pathname: pageProps?.location.pathname,
    params: {
      ...params,
      vendor_id: companySlug ? singleCompanyData?.id : params?.vendor_id,
    },
  });
  const { data: currencyRate } = useGetCurrencyRatesListQuery(
    { currency: params?.currency },
    { skip: !params?.currency || params?.currency === DEFAULT_CURRENCY }
  );

  const productsByCompany = products?.reduce((res, cur) => {
    const currentCompanyData = companies?.[cur.vendorId];

    if (currentCompanyData) {
      return [
        ...res,
        {
          ...currentCompanyData,
          ...cur,
          loanLimitNumberMaxUah: params?.currency
            ? cur.loanLimitNumberMax * (currencyRate?.rate || 0)
            : 0,
          loanLimitNumberMinUah:
            params?.currency !== DEFAULT_CURRENCY
              ? cur?.loanLimitNumberMin * (currencyRate?.rate || 0)
              : 0,
          currencyRate: params?.currency ? currencyRate || {} : 0,
        },
      ];
    }

    return res;
  }, []);

  if (!productsByCompany?.length)
    return {
      isFetching: rest?.status === 'pending' && rest?.status !== 'rejected',
      count: 0,
    };

  const currentSortData = conformitySort?.[params?.sort] || {};

  const sortiedByParamsSort = currentSortData?.key
    ? sortByKey(
        productsByCompany,
        currentSortData.key,
        currentSortData?.direction
      )
    : productsByCompany;

  // Беремо перший елемент СКУ в рамках одного продукту
  const uniqueProducts = currentSortData?.key
    ? sortByKey(
        getUniqueByKey(sortiedByParamsSort, 'productId'),
        currentSortData.key,
        currentSortData?.direction
      )
    : getUniqueByKey(sortiedByParamsSort, 'productId');

  // Для РВК показуємо всі продукти в розхлопнутому вигляді
  const groupByProductId = !companySlug
    ? groupAndCollectByKey(uniqueProducts, 'vendorId')
    : uniqueProducts;

  return {
    products: groupByProductId,
    count: uniqueProducts?.length || 0,
    isFetching:
      (rest?.status === 'pending' && rest?.status === 'rejected') || true,
    ...rest,
  };
};
